import React, { useState } from 'react';
import { BsStars } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { Box, Button, Checkbox, HStack, Stack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import PINSAIOutlineGradientButton from '../../../components/PINSAIOutlineGradientButton';
import { ImportFillTasks } from '../../../constants/importStatuses';
import { createErrorToast, createSuccessToast } from '../../../redux/modules/app';
import { hideModal } from '../../../redux/modules/modal';
import { useClassifyImportFiles } from '../../../services/import-service';

/**
 * Picks what the AI pass may fill before it reads the files. Only blanks are
 * written, as row overrides; archiving is the one task that changes an active file.
 */
const FillBlanksModal = ({ importId }) => {
  const dispatch = useDispatch();
  const { mutateAsync: classify } = useClassifyImportFiles(importId);
  const [tasks, setTasks] = useState(ImportFillTasks.map(task => task.value));

  const toggle = value => setTasks(current => (current.includes(value) ? current.filter(task => task !== value) : [...current, value]));

  // the modal closes at once; the progress bar under the button takes over
  const handleStart = () => {
    dispatch(hideModal());

    classify({ all: false, tasks })
      .then(() => dispatch(createSuccessToast({ title: 'Filling in the blanks', description: 'The files are being read; validate again when it finishes.' })))
      .catch(error => dispatch(createErrorToast({ title: 'The pass could not start', description: error?.message, error })));
  };

  return (
    <Box>
      <Text mb={4} textStyle="bodySm">
        AI reads each staged file and fills the manifest where it is blank. Every value lands as a row override you can change before validating again.
      </Text>

      <Stack spacing={3}>
        {ImportFillTasks.map(task => (
          <Checkbox
            alignItems="flex-start"
            data-testid={`task-${task.value}`}
            isChecked={tasks.includes(task.value)}
            key={task.value}
            sx={{ '.chakra-checkbox__control': { mr: 3, mt: 1 } }}
            onChange={() => toggle(task.value)}
          >
            <Box textAlign="left">
              <Text textStyle="bodyMd500">{task.label}</Text>
              <Text color="greyScale.500" textStyle="bodySm">{task.description}</Text>
            </Box>
          </Checkbox>
        ))}
      </Stack>

      <HStack justifyContent="center" mt={6} spacing={4}>
        <Button variant="secondary" onClick={() => dispatch(hideModal())}>Cancel</Button>
        <PINSAIOutlineGradientButton
          data-testid="start-fill"
          isDisabled={tasks.length === 0}
          leftIcon={BsStars}
          mt={0}
          text="Fill in the Blanks"
          w="auto"
          onClick={handleStart}
        />
      </HStack>
    </Box>
  );
};

FillBlanksModal.propTypes = { importId: PropTypes.number.isRequired };

export default FillBlanksModal;
